const CURRENCY_FORMAT_SETTINGS = {
    EUR: {
        currencySymbol: '€',
        spaceSeparated: true,
    },
    GBP: {
        currencySymbol: '£',
        spaceSeparated: false,
    },
    CHF: {
        currencySymbol: 'CHF',
        spaceSeparated: true,
    },
    TRY: {
        currencySymbol: '₺',
        spaceSeparated: true,
    },
    HUF: {
        currencySymbol: 'Ft',
        spaceSeparated: true,
    },
};

const separateThousands = (str = '') => {
    let arr = [];
    for (let i = str.length; i > 0; i -= 3) {
        arr.unshift(str.substring(i - 3, i));
    }
    return arr.join(',');
};

const formatDecimalPart = (str = '') => {
    return ['0', '0'].reduce((acc, i, j) => acc + (str[j] ? str[j] : i), '');
};

const doFormat = (num, thousandSeparated) => {
    let numParts = num.toString().split('.');
    let whole = thousandSeparated ? separateThousands(numParts[0]) : numParts[0];
    let decimal = formatDecimalPart(numParts[1]);
    return `${whole}.${decimal}`;
};

export const priceFormatter = (priceValue, currencyISOCode = 'EUR', priceFirst = false) => {
    const { currencySymbol = '', spaceSeparated } = CURRENCY_FORMAT_SETTINGS[currencyISOCode] || {};

    return priceFirst
        ? `${doFormat(Math.abs(priceValue), true)}${spaceSeparated ? ' ' : ''}${currencySymbol}`
        : `${currencySymbol}${spaceSeparated ? ' ' : ''}${doFormat(Math.abs(priceValue), true)}`;
};

export const priceFormatterForceNegative = (
    priceValue,
    currencyISOCode = 'EUR',
    showNegative = true,
    currencyPlacement = 'before',
) => {
    const { currencySymbol = '', spaceSeparated } = CURRENCY_FORMAT_SETTINGS[currencyISOCode] || {};

    if (currencyPlacement === 'before') {
        return `${currencySymbol}${spaceSeparated ? ' ' : ''}${showNegative ? '-' : ''}${doFormat(
            Math.abs(priceValue),
        )}`;
    }

    return `${showNegative ? '-' : ''}${doFormat(Math.abs(priceValue))}${
        spaceSeparated ? ' ' : ''
    }${currencySymbol}`;
};

export const getFormattedPrice = price => {
    if (!price) return '';

    const { formattedValue, currencyIso } = price;
    const value = typeof price === 'number' ? price : price.value;

    return (
        formattedValue ||
        (value < 0
            ? priceFormatterForceNegative(value, currencyIso)
            : priceFormatter(value, currencyIso)) ||
        ''
    );
};

export const getFormattedDiscount = price => {
    if (!price) return '';
    const { formattedValue, value, currencyISOCode } = price;
    let formattedDiscount = '';
    if (formattedValue) {
        formattedDiscount = formattedValue.replace(/(^€)(\s)*([0-9]+[,]*[0-9]+)/gm, '$1 -$3');
    } else {
        formattedDiscount = priceFormatterForceNegative(value, currencyISOCode);
    }
    return formattedDiscount;
};

/**
 * Function is used for extracting price fraction part from already formatted price
 *
 * @param {String} price - target object
 * @return {Array} - Start part of price, fraction part, end
 *
 * Usage Example:
 *
 * splitFormatedPrice('$ 10,12');
 * Result: ['$ 10,', '12', '']
 *
 * splitFormatedPrice('10.12 $');
 * Result: ['10.', '12', ' $']
 */

export const splitFormattedPrice = price => {
    const data = /(.+\d*[,|.])(\d*)(.*)/.exec(price);
    const [, start, middle, end] = data || [];
    return data ? [start, middle, end] : null;
};

export const splitNumber = (number, decimalSeparator = '.') => {
    const str = number ? number.toFixed(2) : '00';
    return {
        int: parseInt(number),
        fraction: str.substring(str.indexOf('.') + 1),
        isNumber: !isNaN(parseFloat(number)) && isFinite(number),
        decimalSeparator,
    };
};

export const DEFULT_PRICE_OBJ = { value: 0, currencyISOCode: 'EUR' };

export const convertPointsFormatAccordingToLocale = (locale, points = '0,00') => {
    return (
        points.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) || 0
    );
};

export const formatAmount = (value, precision) => {
    const newValue = Number(value);
    if (Number.isNaN(newValue)) return 0;
    if (newValue % 1 === 0) return newValue;
    return newValue.toFixed(precision);
};

export const otherPricesRemapper = otherPrices => {
    return otherPrices.reduce(
        (acc, el) => ({
            ...acc,
            [el.priceSource]: el,
        }),
        null,
    );
};
